import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, MessageSquare, Send, Trash2, Loader2 } from 'lucide-react';
import { formatRelativeTime, getInitials } from '@/lib/utils';
import { reviewsAPI } from '@/lib/api';
import { toast } from 'sonner';

const ReviewCard = ({ review, currentUserId, onReplyUpdate }) => {
    const [showReplyForm, setShowReplyForm] = useState(false);
    const [replyContent, setReplyContent] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    const isReviewedUser = currentUserId === review.reviewed_user_id;
    const hasReply = !!review.reply;
    
    const handleSubmitReply = async () => {
        if (!replyContent.trim()) {
            toast.error('Răspunsul nu poate fi gol');
            return;
        }
        
        setSubmitting(true);
        try {
            await reviewsAPI.replyToReview(review.review_id, replyContent.trim());
            toast.success('Răspuns adăugat cu succes!');
            setReplyContent('');
            setShowReplyForm(false);
            // Update parent with new reply
            if (onReplyUpdate) {
                onReplyUpdate(review.review_id, {
                    content: replyContent.trim(),
                    created_at: new Date().toISOString()
                });
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la adăugarea răspunsului');
        } finally {
            setSubmitting(false);
        }
    };
    
    const handleDeleteReply = async () => {
        if (!window.confirm('Sigur vrei să ștergi răspunsul?')) return;
        
        setDeleting(true);
        try {
            await reviewsAPI.deleteReply(review.review_id);
            toast.success('Răspuns șters cu succes!');
            // Update parent to remove reply
            if (onReplyUpdate) {
                onReplyUpdate(review.review_id, null);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Eroare la ștergerea răspunsului');
        } finally {
            setDeleting(false);
        }
    };
    
    return (
        <Card className="rounded-2xl">
            <CardContent className="p-6">
                <div className="flex items-start gap-4">
                    <Avatar className="w-10 h-10">
                        <AvatarFallback className="bg-primary/10 text-primary">
                            {getInitials(review.reviewer_name || review.reviewer_username)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <div>
                                <Link 
                                    to={`/user/${review.reviewer_username}`}
                                    className="font-semibold hover:text-primary"
                                >
                                    {review.reviewer_name || review.reviewer_username}
                                </Link>
                                <p className="text-sm text-muted-foreground">
                                    {formatRelativeTime(review.created_at)}
                                </p>
                            </div>
                            <div className="flex items-center gap-0.5">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <Star 
                                        key={star} 
                                        className={`w-4 h-4 ${star <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                                    />
                                ))}
                            </div>
                        </div>
                        {review.comment && (
                            <p className="mt-2 text-foreground">{review.comment}</p>
                        )}
                        {review.listing_title && (
                            <p className="mt-2 text-sm text-muted-foreground">
                                Pentru: <Link to={`/listing/${review.listing_slug || review.listing_id}`} className="text-primary hover:underline">{review.listing_title}</Link>
                            </p>
                        )}
                        
                        {/* Reply section */}
                        {hasReply && (
                            <div className="mt-4 pl-4 border-l-2 border-primary/20 bg-muted/30 p-3 rounded-r-lg">
                                <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-primary">Răspunsul vânzătorului</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">
                                            {formatRelativeTime(review.reply.created_at)}
                                        </span>
                                        {isReviewedUser && (
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6"
                                                onClick={handleDeleteReply}
                                                disabled={deleting}
                                            >
                                                {deleting ? (
                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                    <Trash2 className="w-3 h-3 text-muted-foreground hover:text-destructive" />
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <p className="text-sm text-foreground">{review.reply.content}</p>
                            </div>
                        )}
                        
                        {/* Reply button and form */}
                        {isReviewedUser && !hasReply && (
                            <div className="mt-3">
                                {!showReplyForm ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-muted-foreground hover:text-primary"
                                        onClick={() => setShowReplyForm(true)}
                                    >
                                        <MessageSquare className="w-4 h-4 mr-2" />
                                        Răspunde
                                    </Button>
                                ) : (
                                    <div className="space-y-2">
                                        <Textarea
                                            placeholder="Scrie răspunsul tău..."
                                            value={replyContent}
                                            onChange={(e) => setReplyContent(e.target.value)}
                                            rows={3}
                                            className="resize-none"
                                            maxLength={1000}
                                        />
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-muted-foreground">
                                                {replyContent.length}/1000 caractere
                                            </span>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => {
                                                        setShowReplyForm(false);
                                                        setReplyContent('');
                                                    }}
                                                >
                                                    Anulează
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={handleSubmitReply}
                                                    disabled={submitting || !replyContent.trim()}
                                                >
                                                    {submitting ? (
                                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    ) : (
                                                        <Send className="w-4 h-4 mr-2" />
                                                    )}
                                                    Trimite
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default ReviewCard;
